/**
 * This file contains fallback data in case the CMS is not reachable during production, so the header is still functioning.
 * The latest version of the fallback data is generated at build time, with this file containing another fallback, in case the generation of the fallback data during build time fails.
 */
import { fourOhFourGlobalData } from '@tmh-website/components/system/ErrorCodePage.config';
import { Image, Site } from '@tmh-website/shared/cms';
import { Locale, LocalePrefix, PrefixToLocaleMap } from '@tmh-website/shared/const';

function emptySite(locale: LocalePrefix): Site {
  return {
    ...fourOhFourGlobalData(),
    id: 0,
    locale: PrefixToLocaleMap[locale],
  };
}

export const fallbackGlobalData: Record<LocalePrefix, Site> = {
  [LocalePrefix.De]: {
    id: 10,
    createdAt: '2022-07-05T12:40:03.063Z',
    updatedAt: '2023-11-16T17:44:54.125Z',
    locale: Locale.De,
    metaTitle: 'The Mobility House',
    metaDescription:
      'Finde die passende Ladestation für dein Elektroauto und profitiere von Services rund um Elektromobilität.',
    footerContactSectionText: '<p>Mit unseren Newslettern</p>\n',
    footerContactSectionLink: {
      type: 'Page',
      _type: 'link-field',
      label: 'Jetzt anmelden',
      title: '',
      value: '/newsletter',
      anchor: '',
      attributes: [],
      relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
    },
    footerSpecialLink: {
      type: 'Page',
      _type: 'link-field',
      label: 'Unsere Messetermine',
      title: '',
      value: '/terminvereinbarung-messen',
      anchor: '',
      attributes: [],
      relevantAudiences: ['BusinessPartners', 'PrivateCustomers', 'BusinessCustomers'],
    },
    footerContactSectionHeadline: 'Bleibe in Kontakt',
    homepageLinks: [
      {
        id: 1,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/home',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
        audience: {
          id: 16,
          key: 'PrivateCustomers',
          createdAt: '2022-11-18T18:02:46.700Z',
          updatedAt: '2023-03-10T09:20:04.863Z',
          locale: Locale.De,
          logo: 'TMH Charging',
        },
      },
      {
        id: 4,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/partners',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners'],
        },
        audience: {
          id: 18,
          key: 'BusinessPartners',
          createdAt: '2022-11-18T18:02:54.093Z',
          updatedAt: '2023-03-10T09:20:17.335Z',
          locale: Locale.De,
          logo: 'TMH Solutions',
        },
      },
      {
        id: 5,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/b2b',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessCustomers'],
        },
        audience: {
          id: 17,
          key: 'BusinessCustomers',
          createdAt: '2022-11-18T18:02:50.396Z',
          updatedAt: '2023-03-10T09:35:08.472Z',
          locale: Locale.De,
          logo: 'TMH Solutions',
        },
      },
      {
        id: 11,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/unser-unternehmen',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'OurCompany', 'BusinessCustomers', 'BusinessPartners'],
        },
        audience: {
          id: 32,
          key: 'OurCompany',
          createdAt: '2023-11-14T11:11:20.124Z',
          updatedAt: '2023-11-14T11:12:37.573Z',
          locale: Locale.De,
          logo: 'TMH (default)',
        },
      },
    ],
    navigation: [
      {
        id: 25,
        __component: 'navigation.page-link-group',
        link: {
          id: 148,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Branchen',
            title: '',
            value: '/b2b',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessCustomers'],
          },
        },
        children: [
          {
            id: 149,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Unternehmensflotten',
              title: '',
              value: '/ladeloesungen-fuer-unternehmensflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 150,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Logistikflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-logistik-flotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 158,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Autohandel',
              title: '',
              value: '/ladeloesungen-fuer-autohaeuser',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'BusinessPartners'],
            },
          },
          {
            id: 157,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Elektroinstallationsbetriebe',
              title: '',
              value: '/ladeloesungen-fuer-elektriker',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 153,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Stadtwerke und Energieversorger',
              title: '',
              value: '/ladeloesungen-fuer-stadtwerke-und-energieversorger',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'BusinessPartners'],
            },
          },
          {
            id: 151,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Gewerbeimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-gewerbeimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 152,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Wohnimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-wohnimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 154,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Busflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-busflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 156,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Betreiber',
              title: '',
              value: '/ladeloesungen-fuer-operator',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 159,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Hotels',
              title: '',
              value: '/ladeloesungen-fuer-hotels',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 155,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Leasinggesellschaften',
              title: '',
              value: '/ladeloesungen-fuer-leasinggesellschaft-und-fuhrparkmanagementgesellschaft',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 160,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Fachplaner:innen',
              title: '',
              value: '/ladeloesungen-fuer-fachplaner',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'BusinessPartners'],
            },
          },
        ],
      },
      {
        id: 17,
        __component: 'navigation.page-link-group',
        link: {
          id: 81,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Shop',
            value: '/produkte-services',
            relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 94,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'GREEN WEEKS %',
              title: '',
              value: '/ladestationen/sale.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2805,
              alternativeText: 'GREEN WEEKS @ The Mobility House',
              width: 2540,
              height: 1920,
              hash: '23-10_GREEN_Weeks_Header_Website_2540x1920_DE_9a9979d80dcfa722c003c2b95d14f00b',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/23-10_GREEN_Weeks_Header_Website_2540x1920_DE_9a9979d80dcfa722c003c2b95d14f00b.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 82,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Alle Produkte',
              title: '',
              value: '/produkte-services',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2417,
              alternativeText: 'Eine Frau sitzt in einem Garten und schaut auf ihren Laptop',
              width: 2084,
              height: 1584,
              hash: 'Frau-checked-online-shop-230530-tmh_f1e424c5d3714f1de0d85717f42c7e80',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/Frau-checked-online-shop-230530-tmh_f1e424c5d3714f1de0d85717f42c7e80.png',
              type: 'image',
            } as Image,
          },
          {
            id: 84,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 2418,
              alternativeText: 'Eine Wallbox ist an einem Holzzaun angebracht',
              width: 2560,
              height: 1920,
              hash: '03_Wallboxen_c702f715be33395fae5d20a31dfab30c',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/03_Wallboxen_c702f715be33395fae5d20a31dfab30c.png',
              type: 'image',
            } as Image,
          },
          {
            id: 413,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'KfW 442 Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen-kfw442-foerderung.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2352,
              alternativeText: 'Ein Mann haelt das Ladekabel seines Elektroautos in der Hand ',
              width: 2560,
              height: 1920,
              hash: 'mann-laedt-e-auto-zuhause-230530-tmh_33aadc24dc558105e41484ecb2391a71',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/mann-laedt-e-auto-zuhause-230530-tmh_33aadc24dc558105e41484ecb2391a71.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 90,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Mobile Ladestationen',
              title: '',
              value: '/ladestationen/mobile-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2419,
              alternativeText: 'Eine Frau holt ein Ladekabel aus dem Kofferraum vom E-Auto',
              width: 2560,
              height: 1920,
              hash: 'frau-holt-ladekabel-aus-kofferraum-230531-tmh_39e29e81f45bfe70f876cb9e51b50d2a',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/frau-holt-ladekabel-aus-kofferraum-230531-tmh_39e29e81f45bfe70f876cb9e51b50d2a.png',
              type: 'image',
            } as Image,
          },
          {
            id: 85,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Schnellladestationen',
              title: '',
              value: '/ladestationen/dc-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 2306,
              alternativeText: 'Elektroautos laden an einem Rastplatz',
              width: 2560,
              height: 1920,
              hash: 'E-Autos-laden-an-raststaette-230531-tmh_8e22cc8376b12722acecd79f48a86bc4',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/E-Autos-laden-an-raststaette-230531-tmh_8e22cc8376b12722acecd79f48a86bc4.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 87,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladesäulen',
              value: '/ladestationen/ladesaeulen.html',
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 2422,
              alternativeText: 'Ladesaeule am Straßenrand',
              width: 2560,
              height: 1920,
              hash: '06_Ladesaulen_378495daee6a86ea5a259243c9f58e15',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/06_Ladesaulen_378495daee6a86ea5a259243c9f58e15.png',
              type: 'image',
            } as Image,
          },
          {
            id: 86,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'PV-fähige Wallboxen',
              title: '',
              value: '/ladestationen/pv-faehige-wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2330,
              alternativeText: 'Drohnenaufnahme eines Hauses mit Solarpanelen',
              width: 2560,
              height: 1920,
              hash: 'drohnenaufnahme-haus-solarpanele_42af4de774f7222b26db3b0b73eacb69',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/drohnenaufnahme-haus-solarpanele_42af4de774f7222b26db3b0b73eacb69.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 89,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'E-Bike Ladestationen',
              title: '',
              value: '/ladestationen/e-bike-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 2423,
              alternativeText: 'Wallbox hängt an einem Zaun',
              width: 2560,
              height: 1920,
              hash: '08_E-Bike_589359929b8ddd1f73b3a5ab7ddf441f',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/08_E-Bike_589359929b8ddd1f73b3a5ab7ddf441f.png',
              type: 'image',
            } as Image,
          },
          {
            id: 88,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Set-Angebote',
              title: '',
              value: '/ladestationen/set-angebote.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2340,
              alternativeText: 'Eine Frau laedt ihr Elektroauto zuhause',
              width: 2560,
              height: 1920,
              hash: 'frau-laedt-e-auto-zuhause-230530-tmh_29a42a3d689123b2530ef45c3d268f52',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-laedt-e-auto-zuhause-230530-tmh_29a42a3d689123b2530ef45c3d268f52.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 91,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladekabel',
              title: '',
              value: '/zubehoer/ladekabel.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 2296,
              alternativeText: 'Ein Elektroauto wird geladen und im Hintergrund steht ein Windrad',
              width: 2560,
              height: 1920,
              hash: 'ladendes-e-auto-windrad-im-hintergrund-230531-tmh_4907700862a3e8e9161f9f3db1e390cc',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/ladendes-e-auto-windrad-im-hintergrund-230531-tmh_4907700862a3e8e9161f9f3db1e390cc.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 92,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Zubehör',
              title: '',
              value: '/zubehoer.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2311,
              width: 2560,
              height: 1920,
              hash: 'frau-laedt-sup-mit-strom-aus-e-auto-230531-tmh_048b3e62088f177ef48cbd4d0de8db31',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-laedt-sup-mit-strom-aus-e-auto-230531-tmh_048b3e62088f177ef48cbd4d0de8db31.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 93,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Hersteller',
              title: '',
              value: '/hersteller.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 2425,
              alternativeText: '3 Wallboxen unterschiedlicher Hersteller',
              width: 2268,
              height: 1496,
              hash: '12_Hersteller_eef92505b79698e26783e8cda1373bb5',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/12_Hersteller_eef92505b79698e26783e8cda1373bb5.png',
              type: 'image',
            } as Image,
          },
        ],
      },
      {
        id: 29,
        __component: 'navigation.page-link-group',
        link: {
          id: 179,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Business Partner werden',
            title: '',
            value: '/partners',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessPartners'],
          },
        },
        children: [
          {
            id: 180,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Reseller Programm',
              title: '',
              value: '/partnershop-wallbox-grosshandel',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 181,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'ChargePilot® Partnerprogramm',
              title: '',
              value: '/chargepilot-partnerprogramm',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 182,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Installateur-Netzwerk',
              title: '',
              value: '/installateur-netzwerk',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 183,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Whitelabel-Lösungen',
              title: '',
              value: '/whitelabel-loesungen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 184,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Affiliate Programm',
              title: '',
              value: '/affiliate-programm',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
        ],
      },
      {
        id: 26,
        __component: 'navigation.page-link-group',
        link: {
          id: 161,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'ChargePilot®',
            title: '',
            value: '/chargepilot',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 162,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Übersicht',
              title: '',
              value: '/chargepilot',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 163,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Lastmanagement und Ladelogik',
              title: '',
              value: '/chargepilot/lastmanagement-ladelogik',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'BusinessPartners'],
            },
          },
          {
            id: 165,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Schnittstellen',
              title: '',
              value: '/chargepilot/schnittstellen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 164,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Systemarchitektur',
              title: '',
              value: '/chargepilot/systemarchitektur',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 167,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Betrieb und Monitoring',
              title: '',
              value: '/chargepilot/betrieb-monitoring',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 166,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Service und Support',
              title: '',
              value: '/chargepilot/service-und-support',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 27,
        __component: 'navigation.page-link-group',
        link: {
          id: 168,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Lösungen und Services',
            title: '',
            value: '/beratung-planung-installation-b2b',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessCustomers'],
          },
        },
        children: [
          {
            id: 399,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Beratung, Planung und Installation',
              title: '',
              value: '/beratung-planung-installation-b2c',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
            image: {
              id: 2378,
              alternativeText: 'Ein Mann haelt ein Beratungsgespraech vor einer Wallbox',
              width: 2560,
              height: 1920,
              hash: 'beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 169,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Beratung, Planung und Installation',
              title: '',
              value: '/beratung-planung-installation-b2b',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
            image: {
              id: 2378,
              alternativeText: 'Ein Mann haelt ein Beratungsgespraech vor einer Wallbox',
              width: 2560,
              height: 1920,
              hash: 'beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 170,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Abrechnung',
              title: '',
              value: '/abrechnungsloesungen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
            image: {
              id: 1006,
              alternativeText: 'Frau mit Taschenrechner vor dem Computer',
              width: 4096,
              height: 2731,
              hash: 'Abrechnung_Computer_6819b648718090beb5f9be0fbf0ef7f3',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/Abrechnung_Computer_6819b648718090beb5f9be0fbf0ef7f3.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 171,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Lastmanagement',
              title: '',
              value: '/lade-management/lastmanagement.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
            image: {
              id: 841,
              alternativeText: 'Logistikflotte an Ladestationen aus der Vogelperspektive',
              width: 3359,
              height: 1698,
              hash: 'Logistikflotte_585e16d39a468eebe26273b09d7c64a8',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/Logistikflotte_585e16d39a468eebe26273b09d7c64a8.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 190,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Monitoring',
              title: '',
              value: '/lade-management/monitoring.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 1812,
              alternativeText: 'Elektriker mit Tablet an einer Ladestation',
              width: 2560,
              height: 1920,
              hash: '230530_TMH_Haus_1207_CP_fc6b20279077bf3865958dc610c3df48',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/230530_TMH_Haus_1207_CP_fc6b20279077bf3865958dc610c3df48.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 400,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Solarmanagement',
              title: '',
              value: '/lade-management/photovoltaik-energiemanagement.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2335,
              alternativeText: 'Eine Frau steht vor ihrem ladenden Elektroauto und haelt ihr Handy in ihrer Hand',
              width: 2560,
              height: 1920,
              hash: 'frau-checkt-ladezustand-eyond-app-230530-tmh_72655f85a16bfb60c6e714d415dc38f8',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-checkt-ladezustand-eyond-app-230530-tmh_72655f85a16bfb60c6e714d415dc38f8.jpg',
              type: 'image',
            } as Image,
          },
        ],
      },
      {
        id: 418,
        __component: 'navigation.page-link',
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'eyond',
          title: '',
          value: '/eyond',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
      },
      {
        id: 269,
        __component: 'navigation.page-link',
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Zuhause laden',
          title: '',
          value: '/zuhause-laden',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
        image: {
          id: 2327,
          alternativeText: 'Auto laedt vor Hauseingang',
          width: 2560,
          height: 1920,
          hash: 'auto-laedt-vor-hauseingang-230530-tmh_ff00500e867bb3311abecc50605453ef',
          ext: '.jpg',
          url: 'https://cdn-website.mobilityhouse.com/auto-laedt-vor-hauseingang-230530-tmh_ff00500e867bb3311abecc50605453ef.jpg',
          type: 'image',
        },
      },
      {
        id: 100,
        __component: 'navigation.page-link',
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Knowledge Center',
          title: '',
          value: '/knowledge-center',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
        image: {
          id: 2366,
          alternativeText: 'Mitarbeinde sitzen an einem Tisch mit ihren Laptops',
          width: 2560,
          height: 1920,
          hash: 'mitarbeitende-am-tisch-mit-laptops-230523-tmh_c8337ad9067bfe0035064a4a5221a786',
          ext: '.jpg',
          url: 'https://cdn-website.mobilityhouse.com/mitarbeitende-am-tisch-mit-laptops-230523-tmh_c8337ad9067bfe0035064a4a5221a786.jpg',
          type: 'image',
        },
      },
      {
        id: 101,
        __component: 'navigation.page-link',
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Vehicle-to-Grid',
          title: '',
          value: '/vehicle-to-grid',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
        },
        image: {
          id: 2309,
          width: 2560,
          height: 1920,
          hash: 'frauen-laden-sup-mit-e-auto-strom-230531-tmh_0f1d221cfa3a1365074f11ab92ca160a',
          ext: '.jpg',
          url: 'https://cdn-website.mobilityhouse.com/frauen-laden-sup-mit-e-auto-strom-230531-tmh_0f1d221cfa3a1365074f11ab92ca160a.jpg',
          type: 'image',
        },
      },
      {
        id: 19,
        __component: 'navigation.page-link-group',
        link: {
          id: 102,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Unser Unternehmen',
            title: '',
            value: '/unser-unternehmen',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'OurCompany', 'BusinessCustomers', 'BusinessPartners'],
          },
        },
        children: [
          {
            id: 103,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Über uns',
              title: '',
              value: '/unser-unternehmen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'OurCompany', 'BusinessCustomers', 'BusinessPartners'],
            },
            image: {
              id: 2383,
              alternativeText: 'Eine Frau geht an einer Wand mit Spruch vorbei',
              width: 2560,
              height: 1920,
              hash: 'frau-vor-wand-mit-spruch-230523-tmh_a62e22fd3ee164b7308148a019e2ae6a',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-vor-wand-mit-spruch-230523-tmh_a62e22fd3ee164b7308148a019e2ae6a.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 104,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Karriere',
              title: '',
              value: '/unser-unternehmen/karriere',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers', 'PrivateCustomers', 'OurCompany'],
            },
            image: {
              id: 2387,
              alternativeText: 'Ein Mann zeigt mit der Hand auf einen Trading Screen',
              width: 2560,
              height: 1920,
              hash: 'mann-vor-trading-screens-230523-tmh_6875cbba4038b431e66f05baa8f556aa',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/mann-vor-trading-screens-230523-tmh_6875cbba4038b431e66f05baa8f556aa.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 105,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Newsroom',
              title: '',
              value: '/unser-unternehmen/presse',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2381,
              alternativeText: 'Eine Frau sitzt mit ihrem Laptop an einem Buerotisch',
              width: 2560,
              height: 1920,
              hash: 'frau-mit-laptop-am-buerotisch-230523-tmh_b525fe37df00fb9c166e42eaa8d0f37c',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-mit-laptop-am-buerotisch-230523-tmh_b525fe37df00fb9c166e42eaa8d0f37c.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 106,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Technologie',
              title: '',
              value: '/unser-unternehmen/technologie',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2135,
              alternativeText: 'Bergstraße mit Lichtspuren von Autos',
              width: 3500,
              height: 2333,
              hash: 'mountain-night-landscape-with-light-trails-of-cars-2023-01-26-04-20-00-utc_5cdaaba4a44f5d35711330dc266e7e56',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/mountain-night-landscape-with-light-trails-of-cars-2023-01-26-04-20-00-utc_5cdaaba4a44f5d35711330dc266e7e56.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 107,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Referenzen',
              title: '',
              value: '/unser-unternehmen/referenzen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
            image: {
              id: 254,
              alternativeText: 'Project-partners-on-site-visit.jpeg',
              width: 6720,
              height: 4480,
              hash: 'Project-partners-on-site-visit_9bc45feb4eacf03de7ace8f9f10fbda1',
              ext: '.jpeg',
              url: 'https://cdn-website.mobilityhouse.com/Project-partners-on-site-visit_9bc45feb4eacf03de7ace8f9f10fbda1.jpeg',
              type: 'image',
            } as Image,
          },
          {
            id: 108,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Investoren',
              title: '',
              value: '/unser-unternehmen/investor-relations',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'PrivateCustomers', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2427,
              alternativeText: 'Mann sitzt vor seinem Laptop',
              width: 2560,
              height: 1920,
              hash: '04_Investoren_41f1a48a71b838844229a4413b8bbb0e',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/04_Investoren_41f1a48a71b838844229a4413b8bbb0e.png',
              type: 'image',
            } as Image,
          },
        ],
      },
    ],
    navigationHighlights: [
      {
        id: 29,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Branchen',
          title: '',
          value: '/b2b',
          anchor: 'branchen',
          attributes: [],
          relevantAudiences: ['BusinessCustomers'],
        },
        children: [
          {
            id: 72,
            icon: 'CarFleet',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Unternehmensflotten',
              title: '',
              value: '/ladeloesungen-fuer-unternehmensflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 73,
            icon: 'TruckFleet',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Logistikflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-logistik-flotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 74,
            icon: 'Car',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Autohandel',
              title: '',
              value: '/ladeloesungen-fuer-autohaeuser',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 75,
            icon: 'Installation',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Elektroinstallationsbetriebe',
              title: '',
              value: '/ladeloesungen-fuer-elektriker',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 76,
            icon: 'PrivateCustomer',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Wohnimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-wohnimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 77,
            icon: 'Office2',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Gewerbeimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-gewerbeimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 78,
            icon: 'PowerGrid',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Stadtwerke und EVU',
              title: '',
              value: '/ladeloesungen-fuer-stadtwerke-und-energieversorger',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 79,
            icon: 'BusFleet2',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Busflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-busflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 80,
            icon: 'VariousSet',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Alle Branchen',
              title: '',
              value: '/b2b',
              anchor: 'branchen',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 18,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Business Partner werden',
          title: '',
          value: '/partners',
          anchor: 'programme',
          attributes: [],
          relevantAudiences: ['BusinessPartners'],
        },
        children: [],
      },
      {
        id: 1,
        page: {
          type: 'ShopCategoryPage',
          _type: 'link-field',
          label: 'Ladestationen',
          title: '',
          value: '/ladestationen.html',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
        },
        children: [
          {
            id: 8,
            icon: 'Sale',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'GREEN WEEKS',
              title: '',
              value: '/ladestationen/sale.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 7,
            icon: 'Wallbox',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
          {
            id: 90,
            icon: 'WallboxCheck',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'KfW 442 Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen-kfw442-foerderung.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 1,
            icon: 'MobileCharger',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Mobile Ladestationen',
              title: '',
              value: '/ladestationen/mobile-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 3,
            icon: 'ChargingStationDc',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Schnellladestationen',
              title: '',
              value: '/ladestationen/dc-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
          {
            id: 4,
            icon: 'ChargingStation',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladesäulen',
              title: '',
              value: '/ladestationen/ladesaeulen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
          {
            id: 5,
            icon: 'Sun',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'PV-fähige Wallboxen',
              title: '',
              value: '/ladestationen/pv-faehige-wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 6,
            icon: 'VariousSet',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Set-Angebote',
              title: '',
              value: '/ladestationen/set-angebote.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 9,
            icon: 'Brand',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Hersteller',
              title: '',
              value: '/hersteller.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
        ],
      },
      {
        id: 28,
        page: {
          type: 'ShopCategoryPage',
          _type: 'link-field',
          label: 'Zubehör',
          title: '',
          value: '/zubehoer.html',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
        children: [
          {
            id: 91,
            icon: 'Sale',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'GREEN WEEKS',
              title: '',
              value: '/ladestationen/sale.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 66,
            icon: 'ChargingCable',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladekabel',
              title: '',
              value: '/zubehoer/ladekabel.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 67,
            icon: 'Adapter',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Adapter',
              title: '',
              value: '/zubehoer/adapter.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 69,
            icon: 'Stand',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Standfüße',
              title: '',
              value: '/zubehoer/standfuesse.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 68,
            icon: 'CableHolder',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Kabelhalter',
              title: '',
              value: '/zubehoer/kabelhalter.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 71,
            icon: 'Fuse',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Sicherungsbauteile',
              title: '',
              value: '/zubehoer/sicherungsbauteile.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 70,
            icon: 'Installation',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Sonstiges',
              title: '',
              value: '/zubehoer/sonstiges.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
        ],
      },
      {
        id: 37,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'eyond',
          title: '',
          value: '/eyond',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
        children: [],
      },
      {
        id: 25,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'ChargePilot®',
          title: '',
          value: '/chargepilot',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
        },
        children: [],
      },
      {
        id: 3,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Knowledge Center',
          title: '',
          value: '/knowledge-center',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
        children: [],
      },
      {
        id: 4,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Vehicle-to-Grid',
          title: '',
          value: '/vehicle-to-grid',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
        },
        children: [],
      },
      {
        id: 23,
        page: {
          type: 'URL',
          _type: 'link-field',
          label: 'Partnershop-Login',
          title: '',
          value: 'https://partner.mobilityhouse.com/de_de/customer/account/login',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners'],
        },
        children: [],
      },
      {
        id: 41,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Über uns',
          title: '',
          value: '/unser-unternehmen',
          anchor: '',
          attributes: [],
          relevantAudiences: ['OurCompany'],
        },
        children: [],
      },
      {
        id: 42,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Technologie',
          title: '',
          value: '/unser-unternehmen/technologie',
          anchor: '',
          attributes: [],
          relevantAudiences: ['OurCompany'],
        },
        children: [],
      },
      {
        id: 44,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Newsroom',
          title: '',
          value: '/unser-unternehmen/presse',
          anchor: '',
          attributes: [],
          relevantAudiences: ['OurCompany'],
        },
        children: [],
      },
      {
        id: 43,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Investoren',
          title: '',
          value: '/unser-unternehmen/investor-relations',
          anchor: '',
          attributes: [],
          relevantAudiences: ['OurCompany'],
        },
        children: [],
      },
    ],
    menuQuickLinks: [
      {
        id: 3,
        icon: 'Car',
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'Produkte für dein Elektroauto',
          title: '',
          value: '/elektroautos.html',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
      },
      {
        id: 11,
        icon: 'Money',
        link: {
          type: 'KnowledgeCenterArticle',
          _type: 'link-field',
          label: 'Förderdatenbank',
          title: '',
          value: '/knowledge-center/artikel/foerderung-fuer-elektroautos-und-ladestationen',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
      },
      {
        id: 1,
        icon: 'Search',
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'Kaufberater',
          title: '',
          value: '/online-kaufberater',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
      },
      {
        id: 2,
        icon: 'Installation',
        link: {
          type: 'URL',
          _type: 'link-field',
          label: 'Elektriker:in in deiner Nähe',
          title: '',
          value: 'https://installer-network.mobilityhouse.com/',
          anchor: '',
          attributes: [
            {
              name: 'target',
              value: '_blank',
            },
          ],
          relevantAudiences: ['PrivateCustomers'],
        },
      },
      {
        id: 6,
        icon: 'User',
        link: {
          type: 'URL',
          _type: 'link-field',
          label: 'Partnershop-Login',
          title: '',
          value: 'https://partner.mobilityhouse.com/de_de/customer/account/login',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners'],
        },
      },
    ],
    footerLinkGroups: [
      {
        id: 15,
        columnNumber: '4',
        link: {
          id: 73,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Für Geschäftskund:innen',
            title: '',
            value: '/b2b',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessCustomers'],
          },
        },
        children: [
          {
            id: 236,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Ladelösungen für deine Branche',
              title: '',
              value: '/b2b',
              anchor: 'branchen',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 237,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'ChargePilot®',
              title: '',
              value: '/chargepilot',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 14,
        columnNumber: '3',
        link: {
          id: 64,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Kontakt und Support',
            title: '',
            value: '/kontakt',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 65,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Lieferung und Versand',
              title: '',
              value: '/lieferung-versand',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 66,
            link: {
              type: 'URL',
              _type: 'link-field',
              label: 'Zahlungsarten',
              title: '',
              value: 'https://www.mobilityhouse.com/de_de/lieferung-versand?tab=zahlungsarten',
            },
          },
          {
            id: 67,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Kontakt',
              title: '',
              value: '/kontakt',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 68,
            link: {
              type: 'KnowledgeCenterArticle',
              _type: 'link-field',
              label: 'Förderdatenbank',
              title: '',
              value: '/knowledge-center/artikel/foerderung-fuer-elektroautos-und-ladestationen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
          {
            id: 71,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Fahrzeug-Finder',
              title: '',
              value: '/elektroautos.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 255,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Rücksendung',
              title: '',
              value: '/widerrufsbelehrung',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 69,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Reklamation',
              title: '',
              value: '/kontakt/reklamation',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 70,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Kaufberater',
              title: '',
              value: '/online-kaufberater',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 72,
            link: {
              type: 'URL',
              _type: 'link-field',
              label: 'Elektriker:in in deiner Nähe',
              title: '',
              value: 'https://installer-network.mobilityhouse.com/',
              attributes: [
                {
                  name: 'target',
                  value: '_blank',
                },
              ],
            },
          },
        ],
      },
      {
        id: 13,
        columnNumber: '2',
        link: {
          id: 57,
          link: {
            type: 'ShopCategoryPage',
            _type: 'link-field',
            label: 'Top Hersteller',
            title: '',
            value: '/hersteller.html',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
          },
        },
        children: [
          {
            id: 173,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'ABB Wallboxen',
              title: '',
              value: '/hersteller/abb-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 58,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'ABL Wallboxen',
              title: '',
              value: '/hersteller/abl-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 60,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'EVBox',
              title: '',
              value: '/hersteller/evbox-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 175,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'go-e Charger',
              title: '',
              value: '/hersteller/go-e-charger-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 61,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Heidelberg Wallbox',
              title: '',
              value: '/hersteller/heidelberg-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 174,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Juice Technology',
              title: '',
              value: '/hersteller/juice-technology.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 59,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'KEBA Wallboxen',
              title: '',
              value: '/hersteller/keba-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 177,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'NRGKick',
              title: '',
              value: '/hersteller/nrgkick-ladestation.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 176,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Webasto Wallbox',
              title: '',
              value: '/hersteller/webasto-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 12,
        columnNumber: '1',
        link: {
          id: 56,
          link: {
            type: 'ShopCategoryPage',
            _type: 'link-field',
            label: 'Top Kategorien',
            title: '',
            value: '/ladestationen.html',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 253,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladestationen',
              title: '',
              value: '/ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 254,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
          {
            id: 256,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'KfW 442 Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen-kfw442-foerderung.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 62,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: '11 kW Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen11kw.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
          {
            id: 179,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Schnellladestationen',
              title: '',
              value: '/ladestationen/dc-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
          {
            id: 178,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Mobile Ladestationen',
              title: '',
              value: '/ladestationen/mobile-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 226,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'PV-fähige Wallboxen',
              title: '',
              value: '/ladestationen/pv-faehige-wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 180,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladezubehör',
              title: '',
              value: '/zubehoer.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 227,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladekabel',
              title: '',
              value: '/zubehoer/ladekabel.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
          },
        ],
      },
      {
        id: 16,
        columnNumber: '4',
        link: {
          id: 76,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Für Business Partner',
            title: '',
            value: '/partners',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessPartners'],
          },
        },
        children: [
          {
            id: 77,
            link: {
              type: 'URL',
              _type: 'link-field',
              label: 'Partnershop-Login',
              title: '',
              value: 'https://partner.mobilityhouse.com/de_de/customer/account/login',
              anchor: '',
              attributes: [],
            },
          },
          {
            id: 78,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Reseller werden',
              title: '',
              value: '/partnershop-wallbox-grosshandel',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 79,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Affiliates',
              title: '',
              value: '/affiliate-programm',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
        ],
      },
      {
        id: 17,
        columnNumber: '5',
        link: {
          id: 80,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Unser Unternehmen',
            title: '',
            value: '/unser-unternehmen',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'OurCompany', 'BusinessCustomers', 'BusinessPartners'],
          },
        },
        children: [
          {
            id: 81,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Über uns',
              title: '',
              value: '/unser-unternehmen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'OurCompany', 'BusinessCustomers', 'BusinessPartners'],
            },
          },
          {
            id: 82,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Karriere',
              title: '',
              value: '/unser-unternehmen/karriere',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers', 'PrivateCustomers', 'OurCompany'],
            },
          },
          {
            id: 84,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Newsroom',
              title: '',
              value: '/unser-unternehmen/presse',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
          },
          {
            id: 241,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Referenzen',
              title: '',
              value: '/unser-unternehmen/referenzen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 242,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Investoren',
              title: '',
              value: '/unser-unternehmen/investor-relations',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'PrivateCustomers', 'BusinessCustomers', 'OurCompany'],
            },
          },
        ],
      },
    ],
    footerLegalLinks: [
      {
        id: 85,
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'Impressum',
          title: '',
          value: '/impressum',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
      },
      {
        id: 86,
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'Datenschutz',
          title: '',
          value: '/datenschutz',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
      },
      {
        id: 87,
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'AGB',
          title: '',
          value: '/agb',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
      },
      {
        id: 88,
        link: {
          type: 'URL',
          _type: 'link-field',
          label: 'Widerrufsbelehrung',
          title: '',
          value: 'https://www.mobilityhouse.com/de_de/widerrufsbelehrung?tab=widerrufsbelehrung',
          anchor: '',
          attributes: [],
        },
      },
    ],
    footerPartners: {
      id: 2,
      title: 'Partners',
      partnerLogosEntries: [
        {
          id: 4,
          link: {
            type: 'URL',
            _type: 'link-field',
            label: '',
            value: 'https://www.billiger.de/',
            anchor: '',
            attributes: [
              {
                name: 'target',
                value: '_blank',
              },
            ],
          },
          image: {
            id: 1723,
            alternativeText: 'billiger.de Logo',
            width: 89,
            height: 32,
            hash: 'logo-billiger-de_89a62c2f334616bff5f28cc6b2bbcc88',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-billiger-de_89a62c2f334616bff5f28cc6b2bbcc88.svg',
          },
        },
        {
          id: 5,
          link: {
            type: 'URL',
            _type: 'link-field',
            label: '',
            value: 'https://www.idealo.de/preisvergleich/Shop/304619.html',
            anchor: '',
            attributes: [
              {
                name: 'target',
                value: '_blank',
              },
            ],
          },
          image: {
            id: 1730,
            alternativeText: 'Idealo Partner Logo',
            width: 32,
            height: 32,
            hash: 'logo-idealo-partner_4b855b358fc19e44200833ed6633bea0',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-idealo-partner_4b855b358fc19e44200833ed6633bea0.svg',
          },
        },
        {
          id: 6,
          image: {
            id: 1729,
            alternativeText: 'IT-Recht Kanzlei geprüft',
            width: 190,
            height: 191,
            hash: 'logo-it-kanzlei-verified_1a172e31ceefb415d6142c9af35cba88',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-it-kanzlei-verified_1a172e31ceefb415d6142c9af35cba88.svg',
          },
        },
      ],
    },
    footerLogisticPartners: {
      id: 3,
      title: 'Versandpartner',
      partnerLogosEntries: [
        {
          id: 7,
          image: {
            id: 1726,
            alternativeText: 'UPS Logo',
            width: 28,
            height: 32,
            hash: 'logo-ups_2c1a4026bb0b2df8597321a8fd09ee34',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-ups_2c1a4026bb0b2df8597321a8fd09ee34.svg',
          },
        },
      ],
    },
    footerPaymentPartners: {
      id: 1,
      text: '<p>*Alle Preise inkl. gültiger MwSt. und ggf. zzgl. Versandkosten</p>\n',
      title: 'Sicher bezahlen',
      partnerLogosEntries: [
        {
          id: 1,
          image: {
            id: 1728,
            alternativeText: 'Mastercard Logo',
            width: 42,
            height: 32,
            hash: 'logo-mastercard_12def1d9bbaca651bc9287f662ec8651',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-mastercard_12def1d9bbaca651bc9287f662ec8651.svg',
          },
        },
        {
          id: 2,

          image: {
            id: 1725,
            alternativeText: 'Visa Logo',
            width: 98,
            height: 32,
            hash: 'logo-visa_64ef926e541629eff09c159276e0dbac',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-visa_64ef926e541629eff09c159276e0dbac.svg',
          },
        },
        {
          id: 3,

          image: {
            id: 1727,
            alternativeText: 'PayPal Logo',
            width: 121,
            height: 32,
            hash: 'logo-paypal_87c361313132c2a49dbd7f3cade773cd',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-paypal_87c361313132c2a49dbd7f3cade773cd.svg',
          },
        },
        {
          id: 9,

          image: {
            id: 1778,
            alternativeText: 'Klarna Logo',
            width: 87,
            height: 48,
            hash: 'logo-klarna_85233bcb64bea3f5c2a55ef5e3c2cd9e',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-klarna_85233bcb64bea3f5c2a55ef5e3c2cd9e.svg',
          },
        },
        {
          id: 8,

          image: {
            id: 1779,
            alternativeText: 'Bank Transfer Logo',
            width: 179,
            height: 70,
            hash: 'logo-bank-transfer_4aa20755619625252e29c8b027d45fee',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-bank-transfer_4aa20755619625252e29c8b027d45fee.svg',
          },
        },
        {
          id: 19,

          image: {
            id: 2215,
            alternativeText: 'Logo Apple Pay',
            width: 166,
            height: 106,
            hash: 'Apple_Pay_Mark_RGB_041619_fe4f01ad8e120fb59dd8853931f39e9e',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/Apple_Pay_Mark_RGB_041619_fe4f01ad8e120fb59dd8853931f39e9e.svg',
          },
        },
      ],
    },
    footerCertificates: {
      id: 0,
      partnerLogosEntries: [
        {
          id: 1731,
          image: {
            id: 1731,
            alternativeText: 'Excellent Shop Abzeichen',
            width: 400,
            height: 583,
            hash: 'truested-shop-5-jahre_182e030233693c5a4217129dcfa2a922',
            ext: '.png',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/truested-shop-5-jahre_182e030233693c5a4217129dcfa2a922.png',
          },
        },
        {
          id: 1724,
          image: {
            id: 1724,
            alternativeText: 'Trusted Shops Abzeichen',
            width: 298,
            height: 297,
            hash: 'truested-shop-badge_3679be3b671d89307234f22522f41397',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/truested-shop-badge_3679be3b671d89307234f22522f41397.svg',
          },
        },
      ],
    },
    socialLinks: [
      {
        id: 24,
        type: 'LinkedIn',
        link: 'https://www.linkedin.com/company/the-mobility-house/',
      },
      {
        id: 25,
        type: 'Facebook',
        link: 'https://www.facebook.com/TheMobilityHouse/',
      },
      {
        id: 26,
        type: 'Twitter',
        link: 'https://twitter.com/mobilityhouse',
      },
      {
        id: 27,
        type: 'Instagram',
        link: 'https://www.instagram.com/themobilityhouse/',
      },
      {
        id: 28,
        type: 'YouTube',
        link: 'https://www.youtube.com/@TheMobilityHouse',
      },
    ],
    metaImage: {
      id: 1742,
      alternativeText: '',
      width: 2400,
      height: 1260,
      hash: 'Open-Graph-Image_575018da3f191243c105ceb22aba34e4',
      ext: '.png',
      type: 'image',
      url: 'https://cdn-website.mobilityhouse.com/Open-Graph-Image_575018da3f191243c105ceb22aba34e4.png',
    },
  },
  [LocalePrefix.DeCH]: {
    id: 13,
    createdAt: '2022-07-05T12:41:28.731Z',
    updatedAt: '2023-11-16T17:44:55.017Z',
    locale: Locale.DeCH,
    metaTitle: 'The Mobility House',
    metaDescription:
      'Finde die passende Ladestation für dein Elektroauto und profitiere von Services rund um Elektromobilität.',
    footerContactSectionText: '<p>Mit unseren Newslettern</p>\n',
    footerContactSectionLink: {
      type: 'Page',
      _type: 'link-field',
      label: 'Jetzt anmelden',
      title: '',
      value: '/newsletter',
      anchor: '',
      attributes: [],
      relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
    },
    footerSpecialLink: {
      type: 'Page',
      _type: 'link-field',
      label: 'Unsere Messetermine',
      title: '',
      value: '/terminvereinbarung-messen',
      anchor: '',
      attributes: [],
      relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
    },
    footerContactSectionHeadline: 'Bleibe in Kontakt',
    homepageLinks: [
      {
        id: 6,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/home',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
        audience: {
          id: 28,
          key: 'PrivateCustomers',
          createdAt: '2023-03-10T09:20:04.853Z',
          updatedAt: '2023-03-10T09:20:04.853Z',
          locale: Locale.DeCH,
          logo: 'TMH Charging',
        },
      },
      {
        id: 7,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/partners',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners'],
        },
        audience: {
          id: 29,
          key: 'BusinessPartners',
          createdAt: '2023-03-10T09:20:17.273Z',
          updatedAt: '2023-03-10T09:20:17.273Z',
          locale: Locale.DeCH,
          logo: 'TMH Solutions',
        },
      },
      {
        id: 8,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/b2b',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessCustomers'],
        },
        audience: {
          id: 30,
          key: 'BusinessCustomers',
          createdAt: '2023-03-10T09:20:26.593Z',
          updatedAt: '2023-03-10T09:35:08.472Z',
          locale: Locale.DeCH,
          logo: 'TMH Solutions',
        },
      },
      {
        id: 12,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: '',
          title: '',
          value: '/unser-unternehmen',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
        },
        audience: {
          id: 34,
          key: 'OurCompany',
          createdAt: '2023-11-14T11:12:37.560Z',
          updatedAt: '2023-11-14T11:12:37.560Z',
          locale: Locale.DeCH,
          logo: 'TMH (default)',
        },
      },
    ],
    navigation: [
      {
        id: 54,
        __component: 'navigation.page-link-group',
        link: {
          id: 334,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Branchen',
            title: '',
            value: '/b2b',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessCustomers'],
          },
        },
        children: [
          {
            id: 373,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Unternehmensflotten',
              title: '',
              value: '/ladeloesungen-fuer-unternehmensflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 376,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Logistikflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-logistik-flotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 380,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Autohandel',
              title: '',
              value: '/ladeloesungen-fuer-autohaeuser',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 379,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Elektroinstallationsbetriebe',
              title: '',
              value: '/ladeloesungen-fuer-elektriker',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 374,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Gewerbeimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-gewerbeimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 375,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Wohnimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-wohnimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 378,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Stadtwerke und Energieversorger',
              title: '',
              value: '/ladeloesungen-fuer-stadtwerke-und-energieversorger',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 377,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Busflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-busflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 382,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Betreiber',
              title: '',
              value: '/ladeloesungen-fuer-operator',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 381,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Leasinggesellschaften',
              title: '',
              value: '/ladeloesungen-fuer-leasinggesellschaft-und-fuhrparkmanagementgesellschaft',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 383,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Hotels',
              title: '',
              value: '/ladeloesungen-fuer-hotels',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 384,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Fachplaner:innen',
              title: '',
              value: '/ladeloesungen-fuer-fachplaner',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 49,
        __component: 'navigation.page-link-group',
        link: {
          id: 335,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Shop',
            title: '',
            value: '/produkte-services',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 358,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'GREEN WEEKS',
              title: '',
              value: '/ladestationen/sale.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2431,
              alternativeText: 'Sale Banner mit % Zeichen',
              width: 2560,
              height: 1920,
              hash: '13_Sale_V3_b34b5617a8a50cc6a3bc61c140b6d266',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/13_Sale_V3_b34b5617a8a50cc6a3bc61c140b6d266.png',
              type: 'image',
            } as Image,
          },
          {
            id: 345,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Alle Produkte',
              title: '',
              value: '/produkte-services',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2417,
              alternativeText: 'Eine Frau sitzt in einem Garten und schaut auf ihren Laptop',
              width: 2084,
              height: 1584,
              hash: 'Frau-checked-online-shop-230530-tmh_f1e424c5d3714f1de0d85717f42c7e80',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/Frau-checked-online-shop-230530-tmh_f1e424c5d3714f1de0d85717f42c7e80.png',
              type: 'image',
            } as Image,
          },
          {
            id: 346,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Wallboxen',
              value: '/ladestationen/wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2418,
              alternativeText: 'Eine Wallbox ist an einem Holzzaun angebracht',
              width: 2560,
              height: 1920,
              hash: '03_Wallboxen_c702f715be33395fae5d20a31dfab30c',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/03_Wallboxen_c702f715be33395fae5d20a31dfab30c.png',
              type: 'image',
            } as Image,
          },
          {
            id: 347,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Mobile Ladestationen',
              value: '/ladestationen/mobile-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2419,
              alternativeText: 'Eine Frau holt ein Ladekabel aus dem Kofferraum vom E-Auto',
              width: 2560,
              height: 1920,
              hash: 'frau-holt-ladekabel-aus-kofferraum-230531-tmh_39e29e81f45bfe70f876cb9e51b50d2a',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/frau-holt-ladekabel-aus-kofferraum-230531-tmh_39e29e81f45bfe70f876cb9e51b50d2a.png',
              type: 'image',
            } as Image,
          },
          {
            id: 350,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Schnellladestationen',
              title: '',
              value: '/ladestationen/dc-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2306,
              alternativeText: 'Elektroautos laden an einem Rastplatz',
              width: 2560,
              height: 1920,
              hash: 'E-Autos-laden-an-raststaette-230531-tmh_8e22cc8376b12722acecd79f48a86bc4',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/E-Autos-laden-an-raststaette-230531-tmh_8e22cc8376b12722acecd79f48a86bc4.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 348,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladesäulen',
              value: '/ladestationen/ladesaeulen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2422,
              alternativeText: 'Ladesaeule am Straßenrand',
              width: 2560,
              height: 1920,
              hash: '06_Ladesaulen_378495daee6a86ea5a259243c9f58e15',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/06_Ladesaulen_378495daee6a86ea5a259243c9f58e15.png',
              type: 'image',
            } as Image,
          },
          {
            id: 397,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'PV-fähige Wallboxen',
              title: '',
              value: '/ladestationen/pv-faehige-wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2330,
              alternativeText: 'Drohnenaufnahme eines Hauses mit Solarpanelen',
              width: 2560,
              height: 1920,
              hash: 'drohnenaufnahme-haus-solarpanele_42af4de774f7222b26db3b0b73eacb69',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/drohnenaufnahme-haus-solarpanele_42af4de774f7222b26db3b0b73eacb69.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 354,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Set-Angebote',
              title: '',
              value: '/ladestationen/set-angebote.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2340,
              alternativeText: 'Eine Frau laedt ihr Elektroauto zuhause',
              width: 2560,
              height: 1920,
              hash: 'frau-laedt-e-auto-zuhause-230530-tmh_29a42a3d689123b2530ef45c3d268f52',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-laedt-e-auto-zuhause-230530-tmh_29a42a3d689123b2530ef45c3d268f52.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 355,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladekabel',
              title: '',
              value: '/zubehoer/ladekabel.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2296,
              alternativeText: 'Ein Elektroauto wird geladen und im Hintergrund steht ein Windrad',
              width: 2560,
              height: 1920,
              hash: 'ladendes-e-auto-windrad-im-hintergrund-230531-tmh_4907700862a3e8e9161f9f3db1e390cc',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/ladendes-e-auto-windrad-im-hintergrund-230531-tmh_4907700862a3e8e9161f9f3db1e390cc.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 356,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Zubehör',
              title: '',
              value: '/zubehoer.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2311,
              width: 2560,
              height: 1920,
              hash: 'frau-laedt-sup-mit-strom-aus-e-auto-230531-tmh_048b3e62088f177ef48cbd4d0de8db31',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-laedt-sup-mit-strom-aus-e-auto-230531-tmh_048b3e62088f177ef48cbd4d0de8db31.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 357,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Hersteller',
              title: '',
              value: '/hersteller.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2425,
              alternativeText: '3 Wallboxen unterschiedlicher Hersteller',
              width: 2268,
              height: 1496,
              hash: '12_Hersteller_eef92505b79698e26783e8cda1373bb5',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/12_Hersteller_eef92505b79698e26783e8cda1373bb5.png',
              type: 'image',
            } as Image,
          },
        ],
      },
      {
        id: 53,
        __component: 'navigation.page-link-group',
        link: {
          id: 338,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Business Partner werden',
            title: '',
            value: '/partners',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessPartners'],
          },
        },
        children: [
          {
            id: 369,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Installateur-Netzwerk',
              title: '',
              value: '/installateur-netzwerk',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 370,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Whitelabel-Lösungen',
              title: '',
              value: '/whitelabel-loesungen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 368,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'ChargePilot® Partnerprogramm',
              title: '',
              value: '/chargepilot-partnerprogramm',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
          {
            id: 371,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Affiliate Programm',
              title: '',
              value: '/affiliate-programm',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
        ],
      },
      {
        id: 50,
        __component: 'navigation.page-link-group',
        link: {
          id: 336,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'ChargePilot®',
            title: '',
            value: '/chargepilot',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 361,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Übersicht',
              title: '',
              value: '/chargepilot',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 362,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Lastmanagement und Ladelogik',
              title: '',
              value: '/chargepilot/lastmanagement-ladelogik',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 363,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Schnittstellen',
              title: '',
              value: '/chargepilot/schnittstellen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 365,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Systemarchitektur',
              title: '',
              value: '/chargepilot/systemarchitektur',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 364,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Betrieb und Monitoring',
              title: '',
              value: '/chargepilot/betrieb-monitoring',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 366,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Service und Support',
              title: '',
              value: '/chargepilot/service-und-support',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 56,
        __component: 'navigation.page-link-group',
        link: {
          id: 337,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Lösungen und Services',
            title: '',
            value: '/b2b',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessCustomers'],
          },
        },
        children: [
          {
            id: 391,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Beratung, Planung und Installation',
              title: '',
              value: '/beratung-planung-installation-b2b',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
            image: {
              id: 2378,
              alternativeText: 'Ein Mann haelt ein Beratungsgespraech vor einer Wallbox',
              width: 2560,
              height: 1920,
              hash: 'beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 402,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Beratung, Planung und Installation',
              value: '/beratung-planung-installation-b2c',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
            image: {
              id: 2378,
              alternativeText: 'Ein Mann haelt ein Beratungsgespraech vor einer Wallbox',
              width: 2560,
              height: 1920,
              hash: 'beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/beratungsgespraech-chargepilot-230523-tmh_f9a52a06fbf8d41640e5c3b0fce44c78.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 392,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Abrechnung',
              title: '',
              value: '/abrechnungsloesungen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
            image: {
              id: 1006,
              alternativeText: 'Frau mit Taschenrechner vor dem Computer',
              width: 4096,
              height: 2731,
              hash: 'Abrechnung_Computer_6819b648718090beb5f9be0fbf0ef7f3',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/Abrechnung_Computer_6819b648718090beb5f9be0fbf0ef7f3.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 394,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Lastmanagement',
              value: '/lade-management/lastmanagement.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
            image: {
              id: 841,
              alternativeText: 'Logistikflotte an Ladestationen aus der Vogelperspektive',
              width: 3359,
              height: 1698,
              hash: 'Logistikflotte_585e16d39a468eebe26273b09d7c64a8',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/Logistikflotte_585e16d39a468eebe26273b09d7c64a8.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 393,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Monitoring',
              value: '/lade-management/monitoring.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers', 'PrivateCustomers'],
            },
            image: {
              id: 1812,
              alternativeText: 'Elektriker mit Tablet an einer Ladestation',
              width: 2560,
              height: 1920,
              hash: '230530_TMH_Haus_1207_CP_fc6b20279077bf3865958dc610c3df48',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/230530_TMH_Haus_1207_CP_fc6b20279077bf3865958dc610c3df48.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 401,
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Solarmanagement',
              title: '',
              value: '/lade-management/photovoltaik-energiemanagement.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
            image: {
              id: 2335,
              alternativeText: 'Eine Frau steht vor ihrem ladenden Elektroauto und haelt ihr Handy in ihrer Hand',
              width: 2560,
              height: 1920,
              hash: 'frau-checkt-ladezustand-eyond-app-230530-tmh_72655f85a16bfb60c6e714d415dc38f8',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-checkt-ladezustand-eyond-app-230530-tmh_72655f85a16bfb60c6e714d415dc38f8.jpg',
              type: 'image',
            } as Image,
          },
        ],
      },
      {
        id: 341,
        __component: 'navigation.page-link',
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Zuhause laden',
          title: '',
          value: '/zuhause-laden',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
        image: {
          id: 2327,
          alternativeText: 'Auto laedt vor Hauseingang',
          width: 2560,
          height: 1920,
          hash: 'auto-laedt-vor-hauseingang-230530-tmh_ff00500e867bb3311abecc50605453ef',
          ext: '.jpg',
          url: 'https://cdn-website.mobilityhouse.com/auto-laedt-vor-hauseingang-230530-tmh_ff00500e867bb3311abecc50605453ef.jpg',
          type: 'image',
        },
      },
      {
        id: 340,
        __component: 'navigation.page-link',
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Knowledge Center',
          title: '',
          value: '/knowledge-center',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessCustomers', 'BusinessPartners'],
        },
        image: {
          id: 2366,
          alternativeText: 'Mitarbeinde sitzen an einem Tisch mit ihren Laptops',
          width: 2560,
          height: 1920,
          hash: 'mitarbeitende-am-tisch-mit-laptops-230523-tmh_c8337ad9067bfe0035064a4a5221a786',
          ext: '.jpg',
          url: 'https://cdn-website.mobilityhouse.com/mitarbeitende-am-tisch-mit-laptops-230523-tmh_c8337ad9067bfe0035064a4a5221a786.jpg',
          type: 'image',
        },
      },
      {
        id: 342,
        __component: 'navigation.page-link',
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Vehicle-to-Grid',
          value: '/vehicle-to-grid',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
        },
        image: {
          id: 2309,
          width: 2560,
          height: 1920,
          hash: 'frauen-laden-sup-mit-e-auto-strom-230531-tmh_0f1d221cfa3a1365074f11ab92ca160a',
          ext: '.jpg',
          url: 'https://cdn-website.mobilityhouse.com/frauen-laden-sup-mit-e-auto-strom-230531-tmh_0f1d221cfa3a1365074f11ab92ca160a.jpg',
          type: 'image',
        },
      },
      {
        id: 55,
        __component: 'navigation.page-link-group',
        link: {
          id: 398,
          page: {
            type: 'Page',
            _type: 'link-field',
            label: 'Unser Unternehmen',
            title: '',
            value: '/unser-unternehmen',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
          },
        },
        children: [
          {
            id: 385,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Über uns',
              title: '',
              value: '/unser-unternehmen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2383,
              alternativeText: 'Eine Frau geht an einer Wand mit Spruch vorbei',
              width: 2560,
              height: 1920,
              hash: 'frau-vor-wand-mit-spruch-230523-tmh_a62e22fd3ee164b7308148a019e2ae6a',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-vor-wand-mit-spruch-230523-tmh_a62e22fd3ee164b7308148a019e2ae6a.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 386,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Karriere',
              title: '',
              value: '/unser-unternehmen/karriere',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2387,
              alternativeText: 'Ein Mann zeigt mit der Hand auf einen Trading Screen',
              width: 2560,
              height: 1920,
              hash: 'mann-vor-trading-screens-230523-tmh_6875cbba4038b431e66f05baa8f556aa',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/mann-vor-trading-screens-230523-tmh_6875cbba4038b431e66f05baa8f556aa.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 387,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Newsroom',
              title: '',
              value: '/unser-unternehmen/presse',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2381,
              alternativeText: 'Eine Frau sitzt mit ihrem Laptop an einem Buerotisch',
              width: 2560,
              height: 1920,
              hash: 'frau-mit-laptop-am-buerotisch-230523-tmh_b525fe37df00fb9c166e42eaa8d0f37c',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/frau-mit-laptop-am-buerotisch-230523-tmh_b525fe37df00fb9c166e42eaa8d0f37c.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 388,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Technologie',
              title: '',
              value: '/unser-unternehmen/technologie',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2135,
              alternativeText: 'Bergstraße mit Lichtspuren von Autos',
              width: 3500,
              height: 2333,
              hash: 'mountain-night-landscape-with-light-trails-of-cars-2023-01-26-04-20-00-utc_5cdaaba4a44f5d35711330dc266e7e56',
              ext: '.jpg',
              url: 'https://cdn-website.mobilityhouse.com/mountain-night-landscape-with-light-trails-of-cars-2023-01-26-04-20-00-utc_5cdaaba4a44f5d35711330dc266e7e56.jpg',
              type: 'image',
            } as Image,
          },
          {
            id: 389,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Referenzen',
              title: '',
              value: '/unser-unternehmen/referenzen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
            image: {
              id: 254,
              alternativeText: 'Project-partners-on-site-visit.jpeg',
              width: 6720,
              height: 4480,
              hash: 'Project-partners-on-site-visit_9bc45feb4eacf03de7ace8f9f10fbda1',
              ext: '.jpeg',
              url: 'https://cdn-website.mobilityhouse.com/Project-partners-on-site-visit_9bc45feb4eacf03de7ace8f9f10fbda1.jpeg',
              type: 'image',
            } as Image,
          },
          {
            id: 390,
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Investoren',
              title: '',
              value: '/unser-unternehmen/investor-relations',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'PrivateCustomers', 'BusinessCustomers', 'OurCompany'],
            },
            image: {
              id: 2427,
              alternativeText: 'Mann sitzt vor seinem Laptop',
              width: 2560,
              height: 1920,
              hash: '04_Investoren_41f1a48a71b838844229a4413b8bbb0e',
              ext: '.png',
              url: 'https://cdn-website.mobilityhouse.com/04_Investoren_41f1a48a71b838844229a4413b8bbb0e.png',
              type: 'image',
            } as Image,
          },
        ],
      },
    ],
    navigationHighlights: [
      {
        id: 30,
        page: {
          type: 'URL',
          _type: 'link-field',
          label: 'Branchen',
          title: '',
          value: 'https://www.mobilityhouse.com/ch_de/b2b#branchen',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessCustomers'],
        },
        children: [
          {
            id: 81,
            icon: 'CarFleet',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Unternehmensflotten',
              title: '',
              value: '/ladeloesungen-fuer-unternehmensflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 82,
            icon: 'TruckFleet',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Logistikflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-logistik-flotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 83,
            icon: 'Car',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Autohandel',
              title: '',
              value: '/ladeloesungen-fuer-autohaeuser',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 84,
            icon: 'Installation',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Elektroinstallationsbetriebe',
              title: '',
              value: '/ladeloesungen-fuer-elektriker',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 85,
            icon: 'PrivateCustomer',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Wohnimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-wohnimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 86,
            icon: 'Office2',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Gewerbeimmobilien',
              title: '',
              value: '/ladeloesungen-fuer-gewerbeimmobilien',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 87,
            icon: 'PowerGrid',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Stadtwerke und EVU',
              title: '',
              value: '/ladeloesungen-fuer-stadtwerke-und-energieversorger',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 88,
            icon: 'BusFleet2',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Busflotten',
              title: '',
              value: '/ladeloesungen-fuer-elektrische-busflotten',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 89,
            icon: 'VariousSet',
            page: {
              type: 'Page',
              _type: 'link-field',
              label: 'Alle Branchen',
              title: '',
              value: '/b2b',
              anchor: 'branchen',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 16,
        page: {
          type: 'ShopCategoryPage',
          _type: 'link-field',
          label: 'Ladestationen',
          title: '',
          value: '/ladestationen.html',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
        },
        children: [
          {
            id: 50,
            icon: 'Sale',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'GREEN WEEKS',
              value: '/ladestationen/sale.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 44,
            icon: 'Wallbox',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 45,
            icon: 'MobileCharger',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Mobile Ladestationen',
              title: '',
              value: '/ladestationen/mobile-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 46,
            icon: 'ChargingStationDc',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Schnellladestationen',
              title: '',
              value: '/ladestationen/dc-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 47,
            icon: 'ChargingStation',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladesäulen',
              title: '',
              value: '/ladestationen/ladesaeulen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 58,
            icon: 'Sun',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'PV-fähige Wallboxen',
              title: '',
              value: '/ladestationen/pv-faehige-wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 53,
            icon: 'VariousSet',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Set-Angebote',
              title: '',
              value: '/ladestationen/set-angebote.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 51,
            icon: 'Brand',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Hersteller',
              title: '',
              value: '/hersteller.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 15,
        page: {
          type: 'URL',
          _type: 'link-field',
          label: 'Zubehör',
          title: '',
          value: 'https://www.mobilityhouse.com/ch_de/zubehoer.html',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
        children: [
          {
            id: 92,
            icon: 'Sale',
            page: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'GREEN WEEKS',
              title: '',
              value: '/ladestationen/sale.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 52,
            icon: 'ChargingCable',
            page: {
              type: 'URL',
              _type: 'link-field',
              label: 'Ladekabel',
              title: '',
              value: 'https://www.mobilityhouse.com/ch_de/zubehoer/ladekabel.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 49,
            icon: 'Adapter',
            page: {
              type: 'URL',
              _type: 'link-field',
              label: 'Adapter',
              title: '',
              value: 'https://www.mobilityhouse.com/ch_de/zubehoer/adapter.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 54,
            icon: 'Stand',
            page: {
              type: 'URL',
              _type: 'link-field',
              label: 'Standfüße',
              title: '',
              value: 'https://www.mobilityhouse.com/ch_de/zubehoer/standfuesse.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 55,
            icon: 'CableHolder',
            page: {
              type: 'URL',
              _type: 'link-field',
              label: 'Kabelhalter',
              title: '',
              value: 'https://www.mobilityhouse.com/ch_de/zubehoer/kabelhalter.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 56,
            icon: 'Fuse',
            page: {
              type: 'URL',
              _type: 'link-field',
              label: 'Sicherungsbauteile',
              title: '',
              value: 'https://www.mobilityhouse.com/ch_de/zubehoer/sicherungsbauteile.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 57,
            icon: 'Installation',
            page: {
              type: 'URL',
              _type: 'link-field',
              label: 'Sonstiges',
              title: '',
              value: 'https://www.mobilityhouse.com/ch_de/zubehoer/sonstiges.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
        ],
      },
      {
        id: 27,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'ChargePilot®',
          title: '',
          value: '/chargepilot',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
        },
        children: [],
      },
      {
        id: 19,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Business Partner werden',
          title: '',
          value: '/partners',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners'],
        },
        children: [],
      },
      {
        id: 13,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Knowledge Center',
          title: '',
          value: '/knowledge-center',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessCustomers', 'BusinessPartners'],
        },
        children: [],
      },
      {
        id: 14,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Vehicle-to-Grid',
          title: '',
          value: '/vehicle-to-grid',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
        },
        children: [],
      },
      {
        id: 45,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Über uns',
          value: '/unser-unternehmen',
          title: '',
          anchor: '',
          attributes: [],
          relevantAudiences: ['OurCompany'],
        },
        children: [],
      },
      {
        id: 46,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Technologie',
          title: '',
          value: '/unser-unternehmen/technologie',
          anchor: '',
          attributes: [],
          relevantAudiences: ['BusinessPartners', 'BusinessCustomers', 'OurCompany'],
        },
        children: [],
      },
      {
        id: 47,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Newsroom',
          title: '',
          value: '/unser-unternehmen/presse',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
        },
        children: [],
      },
      {
        id: 48,
        page: {
          type: 'Page',
          _type: 'link-field',
          label: 'Investoren',
          title: '',
          value: '/unser-unternehmen/investor-relations',
          anchor: '',
          attributes: [],
          relevantAudiences: ['OurCompany'],
        },
        children: [],
      },
    ],
    menuQuickLinks: [
      {
        id: 4,
        icon: 'Car',
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'Produkte für dein Elektroauto',
          title: '',
          value: '/elektroautos.html',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers'],
        },
      },
      {
        id: 5,
        icon: 'Installation',
        link: {
          type: 'URL',
          _type: 'link-field',
          label: 'Elektriker:in in deiner Nähe',
          title: '',
          value: 'https://installer-network.mobilityhouse.com/',
          anchor: '',
          attributes: [
            {
              name: 'target',
              value: '_blank',
            },
          ],
          relevantAudiences: ['PrivateCustomers'],
        },
      },
    ],
    footerLinkGroups: [
      {
        id: 40,
        columnNumber: '4',
        link: {
          id: 185,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Für Business Partner',
            title: '',
            value: '/partners',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessPartners'],
          },
        },
        children: [
          {
            id: 215,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Affiliates',
              title: '',
              value: '/affiliate-programm',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners'],
            },
          },
        ],
      },
      {
        id: 41,
        columnNumber: '2',
        link: {
          id: 182,
          link: {
            type: 'ShopCategoryPage',
            _type: 'link-field',
            label: 'Top Hersteller',
            title: '',
            value: '/hersteller.html',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 205,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'ABB Wallboxen',
              title: '',
              value: '/hersteller/abb-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 204,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'ABL Wallboxen',
              title: '',
              value: '/hersteller/abl-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 206,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'EVBox',
              title: '',
              value: '/hersteller/evbox-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 209,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'go-e Charger',
              title: '',
              value: '/hersteller/go-e-charger-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 208,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Heidelberg Wallbox',
              title: '',
              value: '/hersteller/heidelberg-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 210,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Juice Technology',
              title: '',
              value: '/hersteller/juice-technology.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 207,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'KEBA Wallboxen',
              title: '',
              value: '/hersteller/keba-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 211,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'NRGKick',
              title: '',
              value: '/hersteller/nrgkick-ladestation.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 212,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Webasto Wallbox',
              title: '',
              value: '/hersteller/webasto-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 39,
        columnNumber: '4',
        link: {
          id: 183,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Für Geschäftskund:innen',
            title: '',
            value: '/b2b',
            anchor: '',
            attributes: [],
            relevantAudiences: ['BusinessCustomers'],
          },
        },
        children: [
          {
            id: 216,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Ladelösungen für deine Branche',
              title: '',
              value: '/b2b',
              anchor: 'branchen',
              attributes: [],
              relevantAudiences: ['BusinessCustomers'],
            },
          },
          {
            id: 217,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'ChargePilot®',
              title: '',
              value: '/chargepilot',
              anchor: '',
              attributes: [],
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 36,
        columnNumber: '1',
        link: {
          id: 181,
          link: {
            type: 'ShopCategoryPage',
            _type: 'link-field',
            label: 'Top Kategorien',
            title: '',
            value: '/ladestationen.html',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 250,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladestationen',
              title: '',
              value: '/ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 251,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 187,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: '11 kW Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen11kw.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 188,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: '22 kW Wallboxen',
              title: '',
              value: '/ladestationen/wallboxen22kw.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 189,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Mobile Ladestationen',
              title: '',
              value: '/ladestationen/mobile-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 190,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Schnellladestationen',
              title: '',
              value: '/ladestationen/dc-ladestationen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 252,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'PV-fähige Wallboxen',
              title: '',
              value: '/ladestationen/pv-faehige-wallboxen.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 191,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladezubehör',
              title: '',
              value: '/zubehoer.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
          {
            id: 223,
            link: {
              type: 'ShopCategoryPage',
              _type: 'link-field',
              label: 'Ladekabel',
              title: '',
              value: '/zubehoer/ladekabel.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessCustomers'],
            },
          },
        ],
      },
      {
        id: 38,
        columnNumber: '3',
        link: {
          id: 186,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Hilfe und Kontakt',
            title: '',
            value: '/kontakt',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
          },
        },
        children: [
          {
            id: 196,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Lieferung und Versand',
              title: '',
              value: '/lieferung-versand',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 197,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Zahlungsarten',
              title: '',
              value: '/lieferung-versand',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 200,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Fahrzeug-Finder',
              title: '',
              value: '/elektroautos.html',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers'],
            },
          },
          {
            id: 201,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Reklamation',
              title: '',
              value: '/kontakt/reklamation',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 198,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Kontakt',
              title: '',
              value: '/kontakt',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 203,
            link: {
              type: 'URL',
              _type: 'link-field',
              label: 'Elektriker:in in deiner Nähe',
              title: '',
              value: 'https://installer-network.mobilityhouse.com/',
              anchor: '',
              attributes: [],
            },
          },
        ],
      },
      {
        id: 37,
        columnNumber: '5',
        link: {
          id: 184,
          link: {
            type: 'Page',
            _type: 'link-field',
            label: 'Unser Unternehmen',
            title: '',
            value: '/unser-unternehmen',
            anchor: '',
            attributes: [],
            relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
          },
        },
        children: [
          {
            id: 194,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Über uns',
              title: '',
              value: '/unser-unternehmen',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
          },
          {
            id: 192,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Karriere',
              title: '',
              value: '/unser-unternehmen/karriere',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
          },
          {
            id: 193,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Newsroom',
              title: '',
              value: '/unser-unternehmen/presse',
              anchor: '',
              attributes: [],
              relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers', 'OurCompany'],
            },
          },
          {
            id: 239,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Referenzen',
              title: '',
              anchor: '',
              attributes: [],
              value: '/unser-unternehmen/referenzen',
              relevantAudiences: ['BusinessPartners', 'BusinessCustomers'],
            },
          },
          {
            id: 240,
            link: {
              type: 'Page',
              _type: 'link-field',
              label: 'Investoren',
              title: '',
              anchor: '',
              attributes: [],
              value: '/unser-unternehmen/investor-relations',
              relevantAudiences: ['BusinessPartners', 'PrivateCustomers', 'BusinessCustomers', 'OurCompany'],
            },
          },
        ],
      },
    ],
    footerLegalLinks: [
      {
        id: 220,
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'Impressum',
          title: '',
          value: '/impressum',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
      },
      {
        id: 221,
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'Datenschutz',
          title: '',
          value: '/datenschutz',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
      },
      {
        id: 218,
        link: {
          type: 'Page',
          _type: 'link-field',
          label: 'AGB',
          title: '',
          value: '/agb',
          anchor: '',
          attributes: [],
          relevantAudiences: ['PrivateCustomers', 'BusinessPartners', 'BusinessCustomers'],
        },
      },
    ],
    footerPartners: {
      id: 5,
      title: 'Partners',
      partnerLogosEntries: [
        {
          id: 14,
          image: {
            id: 1723,
            alternativeText: 'billiger.de Logo',
            width: 89,
            height: 32,
            hash: 'logo-billiger-de_89a62c2f334616bff5f28cc6b2bbcc88',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-billiger-de_89a62c2f334616bff5f28cc6b2bbcc88.svg',
          },
        },
        {
          id: 15,
          image: {
            id: 1730,
            alternativeText: 'Idealo Partner Logo',
            width: 32,
            height: 32,
            hash: 'logo-idealo-partner_4b855b358fc19e44200833ed6633bea0',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-idealo-partner_4b855b358fc19e44200833ed6633bea0.svg',
          },
        },
        {
          id: 16,
          image: {
            id: 1729,
            alternativeText: 'IT-Recht Kanzlei geprüft',
            width: 190,
            height: 191,
            hash: 'logo-it-kanzlei-verified_1a172e31ceefb415d6142c9af35cba88',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-it-kanzlei-verified_1a172e31ceefb415d6142c9af35cba88.svg',
          },
        },
      ],
    },
    footerLogisticPartners: {
      id: 6,
      title: 'Versandpartner',
      partnerLogosEntries: [
        {
          id: 17,
          image: {
            id: 1780,
            alternativeText: 'Die Post Logo',
            width: 147,
            height: 56,
            hash: 'logo-die-post_895e920c6516cb5246e46bf13c9321dc',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-die-post_895e920c6516cb5246e46bf13c9321dc.svg',
          },
        },
        {
          id: 18,
          image: {
            id: 1782,
            alternativeText: 'Planzer Logo',
            width: 128,
            height: 75,
            hash: 'logo-planzer_afa5fe4d685e0784e9cbb380692095f3',
            ext: '.png',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-planzer_afa5fe4d685e0784e9cbb380692095f3.png',
          },
        },
      ],
    },
    footerPaymentPartners: {
      id: 4,
      text: '<p>*Alle Preise inkl. gültiger MwSt. und ggf. zzgl. Versandkosten</p>\n',
      title: 'Sicher bezahlen',
      partnerLogosEntries: [
        {
          id: 10,
          image: {
            id: 1728,
            alternativeText: 'Mastercard Logo',
            width: 42,
            height: 32,
            hash: 'logo-mastercard_12def1d9bbaca651bc9287f662ec8651',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-mastercard_12def1d9bbaca651bc9287f662ec8651.svg',
          },
        },
        {
          id: 11,
          image: {
            id: 1725,
            alternativeText: 'Visa Logo',
            width: 98,
            height: 32,
            hash: 'logo-visa_64ef926e541629eff09c159276e0dbac',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-visa_64ef926e541629eff09c159276e0dbac.svg',
          },
        },
        {
          id: 13,
          image: {
            id: 1727,
            alternativeText: 'PayPal Logo',
            width: 121,
            height: 32,
            hash: 'logo-paypal_87c361313132c2a49dbd7f3cade773cd',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-paypal_87c361313132c2a49dbd7f3cade773cd.svg',
          },
        },
        {
          id: 12,
          image: {
            id: 1781,
            alternativeText: 'Kauf auf Rechnung Logo',
            width: 175,
            height: 74,
            hash: 'logo-kauf-auf-rechnung_fe4220c3365ad9c577c43026e5c2bba4',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/logo-kauf-auf-rechnung_fe4220c3365ad9c577c43026e5c2bba4.svg',
          },
        },
        {
          id: 20,
          image: {
            id: 2215,
            alternativeText: 'Logo Apple Pay',
            width: 166,
            height: 106,
            hash: 'Apple_Pay_Mark_RGB_041619_fe4f01ad8e120fb59dd8853931f39e9e',
            ext: '.svg',
            url: 'https://cdn-website.mobilityhouse.com/Apple_Pay_Mark_RGB_041619_fe4f01ad8e120fb59dd8853931f39e9e.svg',
            type: 'image',
          },
        },
      ],
    },
    footerCertificates: {
      id: 0,
      partnerLogosEntries: [
        {
          id: 1731,
          image: {
            id: 1731,
            alternativeText: 'Excellent Shop Abzeichen',
            width: 400,
            height: 583,
            hash: 'truested-shop-5-jahre_182e030233693c5a4217129dcfa2a922',
            ext: '.png',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/truested-shop-5-jahre_182e030233693c5a4217129dcfa2a922.png',
          },
        },
        {
          id: 1724,
          image: {
            id: 1724,
            alternativeText: 'Trusted Shops Abzeichen',
            width: 298,
            height: 297,
            hash: 'truested-shop-badge_3679be3b671d89307234f22522f41397',
            ext: '.svg',
            type: 'image',
            url: 'https://cdn-website.mobilityhouse.com/truested-shop-badge_3679be3b671d89307234f22522f41397.svg',
          },
        },
      ],
    },
    socialLinks: [
      {
        id: 39,
        type: 'LinkedIn',
        link: 'https://www.linkedin.com/company/the-mobility-house/',
      },
      {
        id: 42,
        type: 'Facebook',
        link: 'https://www.facebook.com/TheMobilityHouse/',
      },
      {
        id: 40,
        type: 'Twitter',
        link: 'https://twitter.com/mobilityhouse',
      },
      {
        id: 41,
        type: 'Instagram',
        link: 'https://www.instagram.com/themobilityhouse/',
      },
      {
        id: 43,
        type: 'YouTube',
        link: 'https://www.youtube.com/@TheMobilityHouse',
      },
    ],
    metaImage: {
      id: 1742,
      alternativeText: '',
      width: 2400,
      height: 1260,
      hash: 'Open-Graph-Image_575018da3f191243c105ceb22aba34e4',
      ext: '.png',
      type: 'image',
      url: 'https://cdn-website.mobilityhouse.com/Open-Graph-Image_575018da3f191243c105ceb22aba34e4.png',
    },
  },
  [LocalePrefix.En]: emptySite(LocalePrefix.En),
  [LocalePrefix.EnUS]: emptySite(LocalePrefix.EnUS),
  [LocalePrefix.Fr]: emptySite(LocalePrefix.Fr),
  [LocalePrefix.FrCH]: emptySite(LocalePrefix.FrCH),
};
